import GenericTable from 'modules/Tables'

import { IModel } from 'interfaces/model.interface'
import {
  INavigationPages,
  ISingleNavigationPage,
  defaultDashboardConfig,
} from 'interfaces/navigationPage.interface'

import {
  BioriskApprovalsModel,
  BioriskCandidateModel,
} from '../Biorisk/Custom/Biorisk/BioriskModels'
import BarChart from 'modules/Tables/Dashboard/AllDashboards/BarChart'
import { BotulismSlugs } from './BotulismMenu'
import {
  BotulismCandidateModel,
  BotulismGuidelineModel,
} from './BotulismModels'
import IDATrialsMapDashboard from 'components/Dashboards/IDATrialsMapDashboard'
import { IDA360TrialsModel } from '../IDA360/Custom/IDA360/IDA360Models'
import { SortOrders } from 'enums/SortOrders'
import warningMessages from './warningMessages'
import { BotulismCandidatesDetailView } from 'routes/apps/botulism/Candidates'
import { IDAApprovalsDetailView } from 'routes/apps/ida360/Approvals'

type BotulismNavigationPage<TModel extends IModel<any>> = ISingleNavigationPage<
  BotulismSlugs,
  TModel
>

const BioriskBotulismPipeline: BotulismNavigationPage<
  typeof BioriskCandidateModel
> = {
  key: 'BotulismPipeline',
  model: {
    ...BioriskCandidateModel,
    hideToolbar: true,
    tableFitHeight: true,
    quickFilter: 'candidateName',
    displayKey: 'candidateName',
    name: 'Botulism Candidates Competitive Landscape',
    newNote: {
      text: 'Click on Candidates for additional information on trials, approvals, news & press releases, revenue and supply.',
      newUntil: new Date(2025, 1, 1),
    },
  },
  component: GenericTable,
  path: null,
  views: [
    {
      name: 'Default',
      airtableName: 'outbreak_pipeline_botulism',
      airtableBase: 'lzdb',
    },
  ],
  autoGenerateDashboard: {
    ...defaultDashboardConfig,
    hideFilters: true,
    isEmbed: true,
    showSideCards: true,
    demoEnabled: false,
    render: BarChart,
    cardsRight: true,
    hideViewSelector: true,
    exportPageKey: 'BotulismPipelineTable',
    verticalAxisLabel: 'Number of Candidates',
    defaultStackByFilterValue: {
      label: 'Technology Type',
      value: 'type',
    },
    defaultHorizontalFilterValue: {
      label: 'Phase',
      value: 'latestPhaseManual',
    },
    candidateByOptions: [
      { label: 'Phase', value: 'latestPhaseManual' },
      { label: 'Primary Developers', value: 'sponsors' },
      { label: 'Category', value: 'designationB' },
      { label: 'Route of Administration', value: 'routeOfAdministration' },
      { label: 'Tech Type', value: 'type' },
      { label: 'Tech SubType', value: 'type' },
      { label: 'Valency', value: 'vaccineValency' },
      { label: 'Strain', value: 'targetViralStrain' },
    ],
    sortingOrders: {
      latestPhaseManual: [
        'Preclinical',
        'Phase 0',
        'Phase I',
        'Phase I/II',
        'Phase II',
        'Phase II/III',
        'Phase III',
        'Approved',
        'Phase IV',
        'Paused',
        'Not Applicable',
        'Inactive',
        'Not Applicable',
        'Discontinued',
        'Unknown',
      ],
    },
  },
}

const BioriskApprovals: BotulismNavigationPage<typeof BioriskApprovalsModel> = {
  key: 'BotulismApprovals',
  path: null,
  model: {
    ...BioriskApprovalsModel,
    excludeGeneratedCharts: true,
    hideViewSelector: true,
    quickFilter: 'candidateName',
    hideToolbar: true,
    fullHeader: true,
    hideTableHeader: true,
    name: 'Botulism Approvals',
    detailViewType: 'Generic',
    renderDetailView: IDAApprovalsDetailView,
  },
  component: GenericTable,
  views: [
    {
      name: 'All',
      airtableName: 'outbreak_approval_botulism',
      airtableBase: 'lzdb',
    },
  ],
}

const BioriskBotulismPipelineTable: BotulismNavigationPage<
  typeof BotulismCandidateModel
> = {
  key: 'BotulismPipelineTable',
  model: {
    ...BotulismCandidateModel,
    excludeGeneratedCharts: true,
    hideViewSelector: true,
    detailViewType: 'Generic',
    renderDetailView: BotulismCandidatesDetailView,
    hideToolbar: true,
    fullHeader: true,
    hideTableHeader: true,
  },
  component: GenericTable,
  path: null,
  views: [
    {
      name: 'Default',
      airtableName: 'outbreak_pipeline_botulism',
      airtableBase: 'lzdb',
    },
  ],
}
const BotulismGuidelineTable: BotulismNavigationPage<
  typeof BotulismGuidelineModel
> = {
  key: 'BotulismGuidelineTable',
  model: {
    ...BotulismGuidelineModel,
    excludeGeneratedCharts: true,
    hideViewSelector: true,
    detailViewType: 'Generic',
    fullHeader: true,
    defaultSortKey: 'area',
    defaultSortOrder: SortOrders.ASC,
    warningMessage: warningMessages.guidelines,
  },
  component: GenericTable,
  path: null,
  views: [
    {
      name: 'Default',
      airtableName: 'botulism_guideline',
      airtableBase: 'lzdb',
    },
  ],
}

const ClinicalTrials: BotulismNavigationPage<typeof IDA360TrialsModel> = {
  key: 'Trials',
  path: null,
  model: {
    ...IDA360TrialsModel,
    hideTableHeader: true,
    quickFilter: undefined,
  },
  component: GenericTable,
  views: [
    {
      name: 'All',
      airtableName: 'outbreak_botulism',
      airtableBase: 'lzdb',
    },
  ],
  autoGenerateDashboard: {
    ...defaultDashboardConfig,
    render: (props) => (
      <IDATrialsMapDashboard
        {...props}
        progressChartSelectOptions={[
          {
            label: 'Candidate',
            value: 'candidates',
          },

          {
            label: 'Country',
            value: 'areas',
          },
          {
            label: 'Developers',
            value: 'developers',
          },
          {
            label: 'Candidate',
            value: 'candidates',
          },
          {
            label: 'Sponsors',
            value: 'sponsors',
          },
        ]}
      />
    ),

    hideViewSelector: true,
  },
}

export const BotulismPages: INavigationPages<BotulismSlugs> = {
  BioriskBotulismPipeline,
  BioriskBotulismPipelineTable,
  BioriskApprovals,
  BotulismGuidelineTable,
  ClinicalTrials,
}
